/**
 * Vocabulário exibido ao aluno para uma aplicação, conforme sua categoria.
 *
 * O aluno encontra avaliações e listas de exercício pelos mesmos componentes
 * (botão de ação, cards do painel, fluxo de execução). Chamar de "avaliação"
 * uma lista de exercício faz o aluno achar que está prestes a fazer uma prova
 * valendo nota — por isso a copy é resolvida pela **categoria do item**, nunca
 * pela tela em que ele aparece.
 *
 * As frases são devolvidas **completas**, não montadas por concatenação:
 * "exercício" é masculino e "avaliação" é feminino, e colar substantivo com
 * sufixo fixo produziria "exercício já realizada".
 *
 * **Fora deste vocabulário:** o fluxo de NPS (`routes/avaliacao.$id.tsx`,
 * `schemas/nps.ts`, `components/nps/*`, `nps-card`, alertas `nps-*`) usa
 * "avaliação" no sentido de pesquisa de satisfação. Não aplicar nada daqui lá.
 */

import { ExamCategory } from "@/types/test-execution";

export interface ApplicationCopy {
	/** Substantivo para títulos e cabeçalhos: "Avaliação" | "Exercício". */
	noun: string;
	/** Rótulo pleno da categoria, como já usado nas listagens. */
	categoryLabel: string;

	/* Ações (minúsculas, como a UI atual). */
	startAction: string;
	redoAction: string;
	alreadyDone: string;

	/* Botão de ação compartilhado. */
	finishToDownload: string;
	resultUnavailable: string;

	/* Listagem. */
	listSubtitle: string;
	emptyForFilters: string;
	emptyNotFound: string;

	/* Confirmação de refazer. */
	redoTitle: string;
	redoDescription: string;
	redoError: string;

	/* Fluxo de execução. */
	startButton: string;
	loadingLabel: string;
	notFoundLabel: string;
	accessBlocked: string;
	leaveTabWarning: string;
}

const EXAM_COPY: ApplicationCopy = {
	noun: "Avaliação",
	categoryLabel: "Avaliação",

	startAction: "iniciar avaliação",
	redoAction: "refazer avaliação",
	alreadyDone: "prova já realizada",

	finishToDownload: "Finalize a avaliação para baixar o caderno",
	resultUnavailable: "Ainda não é possível acessar os resultados desta prova.",

	listSubtitle:
		"Acompanhe suas avaliações, resultados e agendamentos em um só lugar.",
	emptyForFilters: "Nenhuma avaliação disponível para os filtros selecionados.",
	emptyNotFound: "Nenhuma avaliação encontrada.",

	redoTitle: "Deseja realmente refazer esta avaliação?",
	redoDescription:
		"Todas as suas respostas registradas serão removidas permanentemente e a avaliação será reiniciada do zero.",
	redoError: "Não foi possível reiniciar a avaliação.",

	startButton: "Iniciar prova",
	loadingLabel: "Carregando prova...",
	notFoundLabel: "Esta prova não existe ou foi removida.",
	accessBlocked:
		"Seu acesso a esta avaliação foi bloqueado. Entre em contato com a instituição para mais informações.",
	leaveTabWarning:
		"Você saiu da aba durante a prova. Este evento foi registrado e será enviado para análise. Por favor, permaneça nesta aba até concluir sua avaliação.",
};

const EXERCISE_LIST_COPY: ApplicationCopy = {
	noun: "Exercício",
	categoryLabel: "Lista de exercício",

	startAction: "iniciar exercício",
	redoAction: "refazer exercício",
	alreadyDone: "exercício já realizado",

	finishToDownload: "Finalize o exercício para baixar o caderno",
	resultUnavailable:
		"Ainda não é possível acessar os resultados deste exercício.",

	listSubtitle:
		"Acompanhe seus exercícios, resultados e agendamentos em um só lugar.",
	emptyForFilters: "Nenhum exercício disponível para os filtros selecionados.",
	emptyNotFound: "Nenhum exercício encontrado.",

	redoTitle: "Deseja realmente refazer este exercício?",
	redoDescription:
		"Todas as suas respostas registradas serão removidas permanentemente e o exercício será reiniciado do zero.",
	redoError: "Não foi possível reiniciar o exercício.",

	startButton: "Iniciar exercício",
	loadingLabel: "Carregando exercício...",
	notFoundLabel: "Este exercício não existe ou foi removido.",
	accessBlocked:
		"Seu acesso a este exercício foi bloqueado. Entre em contato com a instituição para mais informações.",
	leaveTabWarning:
		"Você saiu da aba durante o exercício. Este evento foi registrado e será enviado para análise. Por favor, permaneça nesta aba até concluir seu exercício.",
};

/**
 * Termos da categoria.
 *
 * Prova presencial (`3`) não oferece as ações de iniciar e refazer, mas ainda
 * assim recebe o vocabulário de avaliação — nenhuma categoria conhecida cai em
 * texto vazio. Categoria desconhecida também recorre a avaliação, que é o
 * comportamento anterior a esta capability.
 */
export function getApplicationCopy(
	category: ExamCategory | number | null | undefined,
): ApplicationCopy {
	return category === ExamCategory.HOMEWORK ? EXERCISE_LIST_COPY : EXAM_COPY;
}

/** A aplicação é uma lista de exercício. */
export function isExerciseListCategory(
	category: ExamCategory | number | null | undefined,
): boolean {
	return category === ExamCategory.HOMEWORK;
}

/**
 * Título do card de agendamentos do painel, que mistura os dois tipos.
 *
 * Anunciar "novas provas agendadas" para uma lista só de exercícios é o texto
 * que o aluno lê primeiro no painel — adapta ao que a lista de fato traz, sem
 * introduzir um terceiro termo no vocabulário do produto.
 */
export function getScheduledAnnounceTitle(
	categories: Array<ExamCategory | number | null | undefined>,
): string {
	const hasExerciseList = categories.some(isExerciseListCategory);
	const hasExam = categories.some((c) => !isExerciseListCategory(c));

	if (hasExerciseList && !hasExam) {
		return "Você tem novas listas de exercício agendadas";
	}

	if (hasExerciseList && hasExam) {
		return "Você tem novas provas e listas de exercício agendadas";
	}

	return "Você tem novas provas agendadas";
}
